/**
 * FEATURE FLAGS
 * 
 * Dit bestand beheert alle feature flags voor het experimenteren met nieuwe features.
 * Door een flag op false te zetten, wordt alle gerelateerde code uitgeschakeld.
 * 
 * Om een feature volledig terug te draaien:
 * 1. Zet de betreffende flag op false
 * 2. De code blijft staan maar wordt niet uitgevoerd
 * 3. Voor volledige verwijdering: zoek naar "FEATURE: [FEATURE_NAME]" comments
 */

import { isWeb } from './platform';

export const FEATURE_FLAGS = {
  /**
   * BATTLE_MODE
   * 
   * Experimentele feature voor Battle Mode functionaliteit.
   * 
   * Om terug te draaien:
   * - Zet BATTLE_MODE.enabled op false → Battle Mode overal uit
   * - Zet BATTLE_MODE.enabledOnWeb op false → alleen op web uit (native blijft aan)
   * - Voor volledige verwijdering: zoek naar "FEATURE: BATTLE_MODE" in de codebase
   */
  BATTLE_MODE: {
    enabled: true,        // Zet op false om Battle Mode volledig uit te schakelen
    enabledOnWeb: true,  // Zet op false om Battle Mode alleen op web uit te zetten
  },
};

/** Daily/battle games vanaf dit idNumber: gokken op bestaande woorden onthult letters. */
export const DICTIONARY_GUESS_MIN_ID_NUMBER = 1642;

/**
 * Woordenlijst-gokken (letters onthullen via bestaand woord).
 * Daily: idNumber >= 1642. Battle en custom: altijd aan.
 * @param {number|null|undefined} idNumber
 * @param {{ battle?: boolean, custom?: boolean }} [options]
 */
export function isDictionaryGuessEnabled(idNumber, options = {}) {
  if (options.battle || options.custom) return true;
  const n = Number(idNumber);
  if (!Number.isFinite(n)) return false;
  return n >= DICTIONARY_GUESS_MIN_ID_NUMBER;
}

/**
 * Helper functie om te checken of een feature enabled is
 * @param {string} featureName - Naam van de feature (bijv. 'BATTLE_MODE')
 * @returns {boolean} - true als de feature enabled is
 */
export function isFeatureEnabled(featureName) {
  const feature = FEATURE_FLAGS[featureName];
  if (!feature) {
    console.warn(`[FeatureFlags] Feature '${featureName}' niet gevonden`);
    return false;
  }
  return feature.enabled === true;
}

/**
 * Helper functie om Battle Mode specifiek te checken
 * Op web geldt ook enabledOnWeb; op native alleen enabled.
 * @returns {boolean} - true als Battle Mode enabled is op het huidige platform
 */
export function isBattleModeEnabled() {
  if (!isFeatureEnabled('BATTLE_MODE')) return false;
  if (!isWeb()) return true;
  return FEATURE_FLAGS.BATTLE_MODE.enabledOnWeb === true;
}

/**
 * Of Battle Mode op het huidige platform beschikbaar is (native altijd als enabled, web alleen als enabledOnWeb).
 * Gebruik dit voor menu/buttons; gebruik isBattleModeEnabled() voor modal/component-render.
 */
export function isBattleModeAvailableOnPlatform() {
  if (!isBattleModeEnabled()) return false;
  if (!isWeb()) return true; // iOS/Android: altijd tonen als feature aan staat
  return FEATURE_FLAGS.BATTLE_MODE.enabledOnWeb === true;
}
